import React, { useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Copy,
  Mail,
  Loader2,
  RefreshCw,
  ExternalLink,
  Trash2,
  Files,
  Download,
  Printer,
} from 'lucide-react';
import { vendorContractApi } from '../../services/vendorContractService';
import { getVendorStatus } from '../../services/subscriptionService';
import { canForwardInvoiceByEmail } from '../../utils/invoiceFormat';
import { downloadContractPdf } from '../../utils/vendorContractPdf';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';
import ContractBodyHtml from '../../components/vendor/contract/ContractBodyHtml';

const VendorContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const printRef = useRef(null);
  const { success, error: showError } = useToastContext();
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-contract', id],
    queryFn: () => vendorContractApi.getOne(id),
    enabled: !!id,
  });

  const { data: vendorSubRaw } = useQuery({
    queryKey: ['vendor-subscription-status'],
    queryFn: async () => {
      const r = await getVendorStatus();
      return r?.data ?? r;
    },
    staleTime: 60 * 1000,
  });

  const contract = data?.data;
  const canEmail = canForwardInvoiceByEmail(vendorSubRaw);

  const refreshMutation = useMutation({
    mutationFn: () => vendorContractApi.refreshLink(id),
    onSuccess: (res) => {
      success('Link refreshed', 'Previous signing links will no longer work.');
      queryClient.setQueryData(['vendor-contract', id], res);
    },
    onError: (err) => {
      showError('Refresh failed', err?.response?.data?.message || 'Try again.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => vendorContractApi.remove(id),
    onSuccess: () => {
      success('Deleted', 'Contract removed.');
      navigate('/vendor/contracts');
    },
    onError: (err) => {
      showError('Delete failed', err?.response?.data?.message || 'Try again.');
    },
  });

  const copyLink = async () => {
    if (!contract?.signing_url) return;
    try {
      await navigator.clipboard.writeText(contract.signing_url);
      success('Copied', 'Signing link copied — share via SMS, WhatsApp, or chat.');
    } catch {
      showError('Copy failed', 'Select and copy the link manually.');
    }
  };

  const handleDownloadPdf = async () => {
    if (!contract) return;
    setPdfLoading(true);
    try {
      await downloadContractPdf(contract);
      success('Downloaded', 'Contract PDF saved to your device.');
    } catch (e) {
      console.error(e);
      showError('Download failed', 'Could not create PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const openEmailModal = () => {
    setEmailTo(contract?.client_email || '');
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim()) {
      showError('Missing email', 'Enter a recipient email.');
      return;
    }
    setEmailSending(true);
    try {
      const res = await vendorContractApi.sendEmail(id, { email: emailTo.trim() });
      if (res?.success) {
        success('Email sent', res.message || 'Contract emailed for signature.');
        setEmailModalOpen(false);
        queryClient.invalidateQueries({ queryKey: ['vendor-contract', id] });
        queryClient.invalidateQueries({ queryKey: ['vendor-contracts'] });
      } else {
        showError('Could not send', res?.message || 'Try again.');
      }
    } catch (e) {
      showError('Could not send', e?.response?.data?.message || e.message);
    } finally {
      setEmailSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-700 mb-4">Contract not found.</p>
        <Link to="/vendor/contracts" className="text-primary-600 hover:underline">
          Back to contracts
        </Link>
      </div>
    );
  }

  const isSigned = contract.status === 'signed';

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-4 min-w-0 w-full overflow-x-hidden">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <Link
              to="/vendor/contracts"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-3"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to contracts
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">{contract.title}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {contract.contract_number} · {contract.client_name}
              {contract.client_email ? ` · ${contract.client_email}` : ''}
            </p>
            <p className="mt-2">
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize bg-gray-100 text-gray-800">
                {contract.status}
              </span>
              {contract.signed_at && (
                <span className="ml-2 text-xs text-green-700">
                  Signed {new Date(contract.signed_at).toLocaleString()} by {contract.signer_name}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 contract-no-print">
            {isSigned && (
              <>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  loading={pdfLoading}
                  icon={Download}
                >
                  {pdfLoading ? 'Preparing PDF…' : 'Download PDF'}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={handlePrint} icon={Printer}>
                  Print
                </Button>
              </>
            )}
            <Link
              to={`/vendor/contracts/create?from=${contract.id}`}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <Files className="h-3.5 w-3.5" />
              Copy for another client
            </Link>
            {!isSigned && contract.status !== 'cancelled' && (
              <>
                <Button type="button" size="sm" variant="outline" onClick={copyLink} icon={Copy}>
                  Copy link
                </Button>
                {canEmail ? (
                  <Button type="button" size="sm" onClick={openEmailModal} icon={Mail}>
                    Email to sign
                  </Button>
                ) : (
                  <div className="flex flex-col items-end gap-0.5">
                    <button
                      type="button"
                      disabled
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 text-xs font-medium text-gray-400 cursor-not-allowed"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      Email to sign
                    </button>
                    <Link to="/vendor/plan" className="text-[11px] text-primary-600 hover:underline">
                      Upgrade to email contracts
                    </Link>
                  </div>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => refreshMutation.mutate()}
                  loading={refreshMutation.isPending}
                  icon={RefreshCw}
                >
                  New link
                </Button>
                <a
                  href={contract.signing_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 h-8 text-xs font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open sign page
                </a>
              </>
            )}
            {contract.status !== 'signed' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  if (window.confirm('Delete this contract?')) deleteMutation.mutate();
                }}
                icon={Trash2}
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        {!isSigned && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 contract-no-print">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Signing link (share anywhere)
            </p>
            <p className="text-sm text-gray-800 break-all font-mono bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
              {contract.signing_url}
            </p>
          </div>
        )}

        <div
          ref={printRef}
          id="vendor-contract-print-area"
          className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 overflow-hidden min-w-0 w-full"
        >
          <div className="hidden print:block mb-6">
            <h1 className="text-xl font-bold text-gray-900">{contract.title}</h1>
            <p className="text-sm text-gray-600">
              {contract.contract_number} · {contract.client_name}
              {contract.signed_at
                ? ` · Signed ${new Date(contract.signed_at).toLocaleString()} by ${contract.signer_name}`
                : ''}
            </p>
          </div>
          <ContractBodyHtml html={contract.body_html} className="text-gray-800" />
          {isSigned && contract.signature_data && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Signature</p>
              <p className="text-sm text-gray-700 mb-2">
                Signed by <strong>{contract.signer_name}</strong>
                {contract.signed_at
                  ? ` on ${new Date(contract.signed_at).toLocaleString()}`
                  : ''}
              </p>
              <img
                src={contract.signature_data}
                alt="Client signature"
                className="max-w-xs border border-gray-200 rounded-lg bg-white"
              />
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #vendor-contract-print-area, #vendor-contract-print-area * { visibility: visible !important; }
          #vendor-contract-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .contract-no-print { display: none !important; }
        }
      `}</style>

      {emailModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Email contract for signature</h2>
            <p className="text-sm text-gray-600">
              Sends a secure link so the client can review and sign online.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recipient email</label>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="client@example.com"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEmailModalOpen(false)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
              >
                Cancel
              </button>
              <Button type="button" size="sm" onClick={handleSendEmail} disabled={emailSending} icon={Mail}>
                {emailSending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorContractDetail;
